import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Newspaper, FolderPlus } from "lucide-react";

export default function AdminStoreBlogs() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [on, setOn] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catName, setCatName] = useState("");
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/merchant/store-blogs").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
    api.get("/merchant/store-blog/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  };
  useEffect(() => {
    api.get("/merchant/store-blog").then(({ data }) => {
      const enabled = Boolean(data.on);
      setOn(enabled);
      if (enabled) load();
    }).catch(() => setOn(false));
  }, []);

  const filtered = filter ? posts.filter((p) => p.category_id === filter) : posts;
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const someSelected = filtered.some((p) => selected.has(p.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filtered.forEach((p) => n.delete(p.id));
    else filtered.forEach((p) => n.add(p.id));
    return n;
  });

  const del = async (p) => {
    try {
      await api.delete(`/merchant/store-blogs/${p.id}`);
      toast.success("Deleted");
      setSelected((s) => { const n = new Set(s); n.delete(p.id); return n; });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/merchant/store-blogs/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} post${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBulkDeleting(false); }
  };

  const saveCat = async () => {
    try {
      await api.post("/merchant/store-blog/categories", { name: catName.trim() });
      toast.success("Category added");
      setCatOpen(false);
      setCatName("");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const delCat = async (c) => {
    try {
      await api.delete(`/merchant/store-blog/categories/${c.id}`);
      toast.success("Category removed");
      if (filter === c.id) setFilter("");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  if (on === false) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500">App Store</Link>
        <Card className="p-8 text-center text-slate-600" data-testid="blogs-locked">
          Unlock <strong>Blog Section</strong> from the App Store and switch it on to write posts.
        </Card>
      </div>
    );
  }
  if (on === null) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Blogs</h1>
          <p className="text-slate-500 text-sm mt-1">{posts.length} post{posts.length === 1 ? "" : "s"} · Categorise, edit, or delete in bulk</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setCatOpen(true)} data-testid="blogs-new-cat"><FolderPlus className="w-4 h-4 mr-1" /> Category</Button>
          <Link to="/admin/blogs/new"><Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="blogs-new-btn"><Plus className="w-4 h-4 mr-1" /> New blog</Button></Link>
        </div>
      </div>

      {cats.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="blogs-cat-filters">
          <button type="button" onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>All</button>
          {cats.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <button type="button" onClick={() => setFilter(c.id)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`blogs-cat-${c.slug}`}>{c.name}</button>
              <AlertDialog>
                <AlertDialogTrigger asChild><button type="button" className="text-slate-400 hover:text-red-600" data-testid={`blogs-cat-del-${c.slug}`}><Trash2 className="w-3.5 h-3.5" /></button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle><AlertDialogDescription>Posts stay; they become uncategorised.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delCat(c)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </span>
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3" data-testid="blogs-bulk-bar">
          <div className="text-sm font-medium text-slate-700"><span data-testid="blogs-selected-count">{selected.size}</span> selected</div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full bg-white text-red-600 border-red-200" data-testid="blogs-bulk-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete selected</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selected.size} post{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="blogs-bulk-delete-confirm">{bulkDeleting ? "Deleting…" : "Delete"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="blogs-empty">
            <Newspaper className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No posts yet. Write your first story.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="px-4 py-2.5 flex items-center gap-4 bg-slate-50/70 text-xs font-medium text-slate-500">
              <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} data-testid="blogs-select-all" aria-label="Select all" />
              <span>Select all</span>
            </div>
            {filtered.map((p) => (
              <div key={p.id} className={`p-4 flex items-center gap-4 ${selected.has(p.id) ? "bg-blue-50/40" : ""}`} data-testid={`blog-row-${p.slug}`}>
                <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`blog-select-${p.slug}`} />
                <div className="w-16 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0">
                  {p.featured_image_url ? <img src={mediaUrl(p.featured_image_url)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">/{p.slug}{p.category_name ? ` · ${p.category_name}` : ""} · {p.is_published ? "Published" : "Draft"}</div>
                </div>
                <Link to={`/admin/blogs/${p.id}`}><Button variant="outline" size="sm" data-testid={`blog-edit-${p.slug}`}><Pencil className="w-4 h-4" /></Button></Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`blog-delete-${p.slug}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this post?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New blog category</DialogTitle></DialogHeader>
          <div>
            <Label>Name</Label>
            <Input data-testid="blog-cat-name" className="mt-1" value={catName} onChange={(e) => setCatName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCat} data-testid="blog-cat-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
